import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Upload, Loader2, ArrowRight, Award, FileText, Type } from "lucide-react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "./ui/tabs";
import { useAuth, API } from "../lib/auth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function MatchRow({ m }) {
  const url = m.url || `/jobs/${m.id}`;
  const logo = m.company_logo_url ? (m.company_logo_url.startsWith("http") ? m.company_logo_url : `${BACKEND_URL}${m.company_logo_url}`) : null;
  return (
    <Link to={url} data-testid={`match-row-${m.id}`} className="flex items-center gap-3 p-3 rounded-xl hover:bg-slate-50 border border-transparent hover:border-slate-200 transition-colors">
      <div className="h-10 w-10 rounded-lg bg-slate-100 overflow-hidden flex items-center justify-center shrink-0">
        {logo ? <img src={logo} alt="" className="w-full h-full object-contain p-0.5" /> : <span className="text-xs text-slate-400">{(m.company_name || "?").slice(0, 2)}</span>}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-slate-900 truncate">{m.title}</div>
        <div className="text-xs text-slate-500 truncate">{m.company_name} · {m.location} · {m.contract_type}</div>
      </div>
      <div className="shrink-0 flex items-center gap-2">
        <span className={`text-sm font-bold ${m.score >= 70 ? "text-emerald-600" : m.score >= 40 ? "text-amber-600" : "text-slate-400"}`}>{m.score}%</span>
        <ArrowRight className="h-3 w-3 text-slate-400" />
      </div>
    </Link>
  );
}

export default function CVMatcher() {
  const { user, authHeader } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("upload"); // "upload" | "paste"
  const [file, setFile] = useState(null);
  const [text, setText] = useState("");
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [extractedProfile, setExtractedProfile] = useState(null);

  useEffect(() => {
    if (user && user.role === "candidate") {
      axios.get(`${API}/candidate/recommendations`, { headers: authHeader }).then(r => {
        const items = (r.data || []).map(j => ({
          id: j.id,
          title: j.title,
          company_name: j.company_name,
          company_logo_url: j.company_logo_url,
          location: j.location,
          contract_type: j.contract_type,
          url: j.slug_prefix ? `/jobs/${j.slug_prefix}/${j.slug_suffix}` : `/jobs/${j.id}`,
          score: j.match_score,
        }));
        setResults({ matches: items });
      }).catch(() => {});
    }
    // eslint-disable-next-line
  }, [user]);

  const isCandidate = user && user.role === "candidate";
  const ready = mode === "upload" ? !!file : !!text.trim();

  const analyze = async (attempt = 1) => {
    if (!ready) { toast.error(mode === "upload" ? "Upload a CV to continue" : "Write your experience to continue"); return; }
    setLoading(true);
    try {
      const fd = new FormData();
      if (mode === "upload" && file) fd.append("file", file);
      if (mode === "paste" && text.trim()) fd.append("cv_text", text);
      const r = await axios.post(`${API}/match/by-cv`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      setResults(r.data);
      setExtractedProfile(r.data.profile || null);
      if (r.data.ai_used === false) {
        toast.info("Showing quick matches — full analysis from our system is warming up.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const status = e?.response?.status;
      // 5xx / timeout / network → auto-retry once silently
      if (attempt === 1 && (!status || status >= 500 || e?.code === "ECONNABORTED")) {
        setLoading(false);
        await new Promise((r) => setTimeout(r, 1200));
        return analyze(2);
      }
      // Friendly error copy with suggested fallback
      if (detail) {
        toast.error(detail);
      } else if (mode === "upload") {
        toast.error("We couldn't read this CV. Try a clearer PDF/DOCX, or switch to the 'Write your experience' tab.");
      } else {
        toast.error("Could not analyze your text right now. Please retry in a moment.");
      }
    }
    setLoading(false);
  };

  const createAccount = () => {
    if (!extractedProfile) return;
    try {
      const wh = extractedProfile.work_history;
      const whString = Array.isArray(wh) ? wh.join("\n") : (typeof wh === "string" ? wh : "");
      const firstWorkLine = whString.split(/\r?\n/).map(s => s.trim()).filter(Boolean)[0] || "";
      const inferredCompany = firstWorkLine.split(/[—–\-|·]/)[0]?.trim() || "";
      const prefill = { ...extractedProfile, work_history: whString, company: extractedProfile.company || inferredCompany };
      sessionStorage.setItem("motravay_prefill", JSON.stringify(prefill));
      navigate("/register?from=matcher");
    } catch (e) {
      toast.error("Could not start signup");
    }
  };

  const showResults = isCandidate || results !== null;

  return (
    <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-testid="cv-matcher">
      <div className="text-center mb-8">
        <span className="text-xs uppercase tracking-[0.25em] text-[#1A8FE3] font-semibold">Our system · Instant CV Matcher</span>
        <h2 className="font-display text-3xl sm:text-4xl font-bold text-slate-900 mt-2">
          {isCandidate ? "Jobs picked for you" : "Drop your CV — see the best matches in seconds"}
        </h2>
        <p className="mt-3 text-slate-600 max-w-2xl mx-auto">
          {isCandidate
            ? "Personalised job recommendations based on your saved profile."
            : "Our system extracts your profile, scores every open role on Motravay, and shows your best matches instantly — no signup needed."}
        </p>
      </div>

      <div className={`grid grid-cols-1 ${showResults && !isCandidate ? "lg:grid-cols-5" : ""} gap-6`}>
        {!isCandidate && (
          <Card className={`p-6 ${showResults ? "lg:col-span-2" : "max-w-2xl mx-auto w-full"}`}>
            <h3 className="font-display text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-[#1A8FE3]" />Analyse my CV
            </h3>

            <Tabs value={mode} onValueChange={setMode} className="mt-4">
              <TabsList className="grid grid-cols-2 w-full bg-slate-100">
                <TabsTrigger value="upload" data-testid="matcher-tab-upload" className="data-[state=active]:bg-white">
                  <FileText className="h-3.5 w-3.5 mr-1.5" />Upload File
                </TabsTrigger>
                <TabsTrigger value="paste" data-testid="matcher-tab-paste" className="data-[state=active]:bg-white">
                  <Type className="h-3.5 w-3.5 mr-1.5" />Write your experience
                </TabsTrigger>
              </TabsList>

              <TabsContent value="upload" className="mt-4">
                <label
                  htmlFor="matcher-file-input"
                  className={`flex flex-col items-center justify-center gap-2 border-2 border-dashed rounded-xl px-4 py-10 cursor-pointer transition-colors ${file ? "border-[#1A8FE3] bg-[#1A8FE3]/5" : "border-slate-300 hover:border-[#1A8FE3] hover:bg-slate-50"}`}
                >
                  <Upload className="h-6 w-6 text-slate-500" />
                  <span className="text-sm text-slate-700 font-medium">{file ? file.name : "Drop or click to upload"}</span>
                  <span className="text-xs text-slate-400">PDF · DOCX · TXT (max 10 MB)</span>
                  <input
                    id="matcher-file-input"
                    data-testid="matcher-file"
                    type="file"
                    accept=".pdf,.doc,.docx,.txt"
                    className="hidden"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                  />
                </label>
              </TabsContent>

              <TabsContent value="paste" className="mt-4">
                <p className="text-xs text-slate-500 mb-2">
                  No CV handy? Tell us about your job experiences and skills — Maya will use it to find your best matches.
                </p>
                <Textarea
                  data-testid="matcher-text"
                  rows={8}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={"e.g. I'm a chef with 6 years of experience in hotels in Mauritius. I lead a kitchen team of 5, speak French and English, and have worked at Hilton, LUX*, and a beach resort in Flic-en-Flac. I'm looking for a sous-chef or head chef role."}
                  className="resize-none"
                />
              </TabsContent>
            </Tabs>

            <Button
              onClick={analyze}
              disabled={loading}
              data-testid="matcher-analyze"
              className="mt-4 w-full bg-[#1A8FE3] hover:bg-[#157AC0] text-white rounded-full h-12 text-sm font-semibold disabled:opacity-90"
            >
              {loading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Find My Matches
                </>
              )}
            </Button>

            {extractedProfile && (
              <div className="mt-4 p-4 rounded-xl bg-sky-50 border border-sky-100" data-testid="matcher-conversion">
                <p className="text-sm text-slate-700">
                  Creating an account enables you to apply easily to multiple roles and get smart job recommendations based on your profile.
                </p>
                <Button onClick={createAccount} data-testid="matcher-create-account" className="mt-3 bg-black text-white rounded-full h-9 px-4 text-xs">
                  Create my Motravay account <ArrowRight className="ml-1 h-3 w-3" />
                </Button>
              </div>
            )}
          </Card>
        )}

        {showResults && (
          <Card className={`p-6 ${isCandidate ? "" : "lg:col-span-3"}`} data-testid="matcher-results">
            <h3 className="font-display text-lg font-semibold text-slate-900 inline-flex items-center gap-2">
              <Award className="h-4 w-4 text-[#1A8FE3]" />Top matches
            </h3>
            {!results ? (
              <div className="mt-8 text-center text-slate-400 text-sm">
                {isCandidate ? "Loading your recommendations…" : "Your top matches will appear here."}
              </div>
            ) : results.matches?.length === 0 ? (
              <div className="mt-8 text-center text-slate-400 text-sm">No matching jobs found. Try a different CV or browse all jobs.</div>
            ) : (
              <div className="mt-4 space-y-1.5">
                {results.matches.slice(0, 8).map(m => <MatchRow key={m.id} m={m} />)}
              </div>
            )}
          </Card>
        )}
      </div>
    </section>
  );
}
